import React, { type SyntheticEvent, useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import Pagination from '@atlaskit/pagination';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useArchiveIssueActions } from '../../../../controllers/main.tsx';
import messages from './messages.tsx';

export const PaginationControls = ({
	currentPage,
	totalPages,
}: {
	currentPage: number;
	totalPages: number;
}) => {
	const { formatMessage } = useIntl();

	const { setCurrentPage, clearIssuesInCurrentPage } = useArchiveIssueActions();

	const pages = useMemo<number[]>(() => {
		const firstPage = 1;
		const lastPage = totalPages ?? 1;
		return Array(lastPage - firstPage + 1)
			.fill(undefined)
			.map((_, i) => firstPage + i);
	}, [totalPages]);

	const onChange = useCallback(
		(_: SyntheticEvent, page: number) => {
			if (page === currentPage) {
				return;
			}
			setCurrentPage(page);
			clearIssuesInCurrentPage();
		},
		[clearIssuesInCurrentPage, currentPage, setCurrentPage],
	);

	return (
		<UFOSegment name="issue-view-archived-issues-paginated-table">
			<JSErrorBoundary
				id="issue.issue-view.activity.archived-issues-paginated-table"
				packageName="jiraArchivedIssues"
				teamName="kenji"
				fallback="unmount"
			>
				<PaginationContainer>
					<Pagination
						testId="issue-archival-archives.ui.archives-table.footer-renderer.pagination-controls.pagination-controls"
						pages={pages}
						onChange={onChange}
						selectedIndex={currentPage - 1}
						max={7}
						nextLabel={formatMessage(messages.nextButton)}
						previousLabel={formatMessage(messages.prevButton)}
					/>
				</PaginationContainer>
			</JSErrorBoundary>
		</UFOSegment>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PaginationContainer = styled.div({
	display: 'flex',
	flex: 1,
	justifyContent: 'flex-end',
});
